import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Markdown } from "@/components/ui/markdown";
import { toast } from "@/components/ui/use-toast";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/i18n";
import { useApiQuery } from "@/lib/use-api-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export type ProjectVisionData = {
  vision: string | null;
  grandGoal: string | null;
  approvedAt: string | null;
  approvedBy: { id: string; name: string | null } | null;
  draft: string | null;
  draftedAt: string | null;
  canEdit: boolean;
};

/** The grand goal under the project name; the manager approves a pending draft here. */
export function ProjectVision({ projectId }: { projectId: string }) {
  const query = useApiQuery(
    () => api.get<ProjectVisionData>(`/api/projects/${projectId}/vision`),
    [projectId],
  );
  return query.data ? (
    <ProjectVisionView projectId={projectId} data={query.data} onChanged={query.reload} />
  ) : null;
}

export function ProjectVisionView({
  projectId,
  data,
  onChanged,
}: {
  projectId: string;
  data: ProjectVisionData;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(data.draft ?? "");
  const [busy, setBusy] = useState(false);

  if (!data.grandGoal && !data.canEdit) return null;

  async function run(action: () => Promise<void>) {
    setBusy(true);
    try {
      await action();
      onChanged();
    } catch {
      toast({ title: t("projVision.saveFailed"), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  }

  const approve = () =>
    run(async () => {
      const res = await api.post<{ wikiWritten: boolean; reason?: string }>(
        `/api/projects/${projectId}/vision/approve`,
        { text },
      );
      if (res.wikiWritten) toast({ title: t("projVision.approved") });
      else {
        toast({
          title: t("projVision.wikiNotWritten", { reason: res.reason ?? "" }),
          variant: "destructive",
        });
      }
      setOpen(false);
    });
  const discard = () =>
    run(async () => {
      await api.post(`/api/projects/${projectId}/vision/discard`, {});
      setOpen(false);
    });
  const propose = () =>
    run(async () => {
      await api.post(`/api/projects/${projectId}/vision/draft`, {});
      toast({ title: t("projVision.proposeStarted") });
    });

  return (
    <div className="mt-1 flex max-w-3xl flex-wrap items-baseline gap-x-3 gap-y-1">
      {data.grandGoal && (
        <p className="text-sm text-muted-foreground" data-testid="project-grand-goal">
          {data.grandGoal}
        </p>
      )}
      {(data.vision || data.draft) && (
        <Button
          type="button"
          variant={data.draft && data.canEdit ? "outline" : "link"}
          size="sm"
          className={data.draft && data.canEdit ? "h-7" : "h-auto p-0 text-xs"}
          onClick={() => {
            setText(data.draft ?? data.vision ?? "");
            setOpen(true);
          }}
        >
          {data.draft && data.canEdit ? t("projVision.draftPending") : t("projVision.open")}
        </Button>
      )}
      {!data.vision && !data.draft && data.canEdit && (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0 text-xs"
          disabled={busy}
          onClick={() => void propose()}
        >
          {t("projVision.propose")}
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t("projVision.title")}</DialogTitle>
            <DialogDescription>{t("projVision.intro")}</DialogDescription>
          </DialogHeader>
          {data.vision && data.approvedAt && (
            <p className="text-xs text-muted-foreground">
              {t("projVision.approvedBy", {
                name: data.approvedBy?.name ?? "",
                date: formatDate(data.approvedAt),
              })}
            </p>
          )}
          {data.draft && data.canEdit ? (
            <div className="space-y-2">
              <p className="text-sm font-medium">{t("projVision.draftTitle")}</p>
              <p className="text-xs text-muted-foreground">{t("projVision.draftHint")}</p>
              <textarea
                className="h-80 w-full rounded-md border bg-background p-2 font-mono text-sm"
                value={text}
                aria-label={t("projVision.text")}
                onChange={(e) => setText(e.target.value)}
              />
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto">
              {data.vision ? (
                <Markdown content={data.vision} />
              ) : (
                <p className="text-sm text-muted-foreground">{t("projVision.none")}</p>
              )}
            </div>
          )}
          <DialogFooter>
            {data.draft && data.canEdit ? (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => void discard()}
                >
                  {t("projVision.discard")}
                </Button>
                <Button
                  type="button"
                  disabled={busy || text.trim().length < 20}
                  onClick={() => void approve()}
                >
                  {t("projVision.approve")}
                </Button>
              </>
            ) : (
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                {t("projVision.close")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
